import { ChevronDown } from "lucide-react";
import { useId, useState } from "react";

import { EmailRow } from "./EmailRow";
import {
  ONE_OFF_EVENT_SLUG,
  ORGANISATION_EMAIL_TYPES,
  SEPTEMBER_VISIT_EMAIL_TYPES,
  VISIT_FOLLOW_UP_EMAIL_TYPES,
  VISIT_THANK_YOU_EMAIL_TYPES,
} from "./emailTypes";
import { CommunityInvitePanel } from "./one-off/CommunityInvitePanel";
import { SeptemberVisitPanel } from "./one-off/SeptemberVisitPanel";
import { VisitFollowUpPanel } from "./one-off/VisitFollowUpPanel";
import { VisitThankYouPanel } from "./one-off/VisitThankYouPanel";
import { lastSentLine, type EmailSend } from "./sendHistory";
import { BUTTON_DISCLOSURE } from "./ui";

/** After the end of 20 September in Sydney the community invitation has
 *  nothing left to invite people to. */
const COMMUNITY_INVITE_UNTIL = Date.parse("2026-09-20T14:00:00Z");

/** After the end of 26 September in Sydney the visit is over. */
const SEPTEMBER_VISIT_UNTIL = Date.parse("2026-09-26T14:00:00Z");

/** The thank-you appears once the last evening, in Christchurch, is over,
 *  and stays until the end of October. */
const VISIT_THANK_YOU_FROM = Date.parse("2026-09-26T11:00:00Z");
const VISIT_THANK_YOU_UNTIL = Date.parse("2026-10-31T13:00:00Z");

/** The follow-up (photos, feedback form, donations) comes after the
 *  thank-you, from 8 October, and stays until the end of November. */
const VISIT_FOLLOW_UP_FROM = Date.parse("2026-10-07T13:00:00Z");
const VISIT_FOLLOW_UP_UNTIL = Date.parse("2026-11-30T13:00:00Z");

type OneOffId = "follow-up" | "thanks" | "visit" | "community";

/**
 * Emails sent once, for the September visit rather than for one event.
 * Closed by default, after the event emails. Each drops off the page once
 * its date has passed, but only once the history has loaded: while it
 * loads or fails they stay listed, so they can't vanish by accident.
 */
export function OneOffEmails({
  history,
  historyLoaded,
  isOrganiser,
  defaultTestEmail,
  senderName,
}: {
  history: readonly EmailSend[] | undefined;
  historyLoaded: boolean;
  /** The community invitation is for organisers only. */
  isOrganiser: boolean;
  defaultTestEmail: string;
  senderName: string | null;
}) {
  const headingId = useId();
  const [shown, setShown] = useState(false);
  const [openEmail, setOpenEmail] = useState<OneOffId | null>(null);

  const now = Date.now();
  const showFollowUp =
    now >= VISIT_FOLLOW_UP_FROM &&
    !(historyLoaded && now > VISIT_FOLLOW_UP_UNTIL);
  const showThanks =
    now >= VISIT_THANK_YOU_FROM &&
    !(historyLoaded && now > VISIT_THANK_YOU_UNTIL);
  const showVisit = !(historyLoaded && now > SEPTEMBER_VISIT_UNTIL);
  const showCommunity =
    isOrganiser && !(historyLoaded && now > COMMUNITY_INVITE_UNTIL);
  const count =
    Number(showFollowUp) +
    Number(showThanks) +
    Number(showVisit) +
    Number(showCommunity);
  if (count === 0) {
    return null;
  }

  const sentLine = (types: readonly string[]): string | null =>
    lastSentLine(history, ONE_OFF_EVENT_SLUG, types);
  const toggle = (id: OneOffId) =>
    setOpenEmail((open) => (open === id ? null : id));

  return (
    <section aria-labelledby={headingId} className="mt-12">
      <h2 id={headingId}>
        <button
          type="button"
          onClick={() => setShown((v) => !v)}
          aria-expanded={shown}
          className={BUTTON_DISCLOSURE}
        >
          <span>One-off emails ({count})</span>
          <ChevronDown
            size={18}
            aria-hidden
            className={`shrink-0 transition-transform ${shown ? "rotate-180" : ""}`}
          />
        </button>
      </h2>
      {shown ? (
        <ul className="mt-3 space-y-3">
          {showFollowUp ? (
            <EmailRow
              level={3}
              title="Photos, feedback and online meditation"
              audienceLine="Goes to everyone who registered for an Australian September event"
              statusLine={sentLine(VISIT_FOLLOW_UP_EMAIL_TYPES)}
              open={openEmail === "follow-up"}
              onToggle={() => toggle("follow-up")}
            >
              <VisitFollowUpPanel
                defaultTestEmail={defaultTestEmail}
                senderName={senderName}
              />
            </EmailRow>
          ) : null}
          {showThanks ? (
            <EmailRow
              level={3}
              title="Thank you for the September visit"
              audienceLine="Goes to everyone who registered for any September event, in every city"
              statusLine={sentLine(VISIT_THANK_YOU_EMAIL_TYPES)}
              open={openEmail === "thanks"}
              onToggle={() => toggle("thanks")}
            >
              <VisitThankYouPanel
                defaultTestEmail={defaultTestEmail}
                senderName={senderName}
              />
            </EmailRow>
          ) : null}
          {showVisit ? (
            <EmailRow
              level={3}
              title="September visit announcement"
              audienceLine="Goes to everyone we have an email address for"
              statusLine={sentLine(SEPTEMBER_VISIT_EMAIL_TYPES)}
              open={openEmail === "visit"}
              onToggle={() => toggle("visit")}
            >
              <SeptemberVisitPanel
                defaultTestEmail={defaultTestEmail}
                senderName={senderName}
              />
            </EmailRow>
          ) : null}
          {showCommunity ? (
            <EmailRow
              level={3}
              title="Community leaders invitation, 18 and 20 September"
              audienceLine="Goes to the community organisations on our list"
              statusLine={sentLine(ORGANISATION_EMAIL_TYPES)}
              open={openEmail === "community"}
              onToggle={() => toggle("community")}
            >
              <CommunityInvitePanel defaultTestEmail={defaultTestEmail} />
            </EmailRow>
          ) : null}
        </ul>
      ) : (
        <p className="mt-1.5 text-[14px] text-[color:var(--color-ink-soft)]">
          Emails for the September visit as a whole, each sent once.
        </p>
      )}
    </section>
  );
}
